import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  type ChainFullId,
  ChainFullIds,
  ChainNamespaces,
  type EstimatedAccountOperations,
  type FA2Transfer,
  type UnifiedNFTBalance,
  parseChainFullId,
} from "@umami/chains";
import { FormProvider } from "react-hook-form";

import { NFTTile } from "./NFTTile";
import { useColor } from "../../../styles/useColor";
import { AddressTile } from "../../AddressTile/AddressTile";
import { SafeSendTile } from "../../SafeSend/SafeSendTile";
import { SignButton } from "../SignButton";
import { AsyncSignPageFee } from "../SignPageFee";
import { SignPageHeader } from "../SignPageHeader";
import { getSender, useSignPageHelpers } from "../utils";

export const SignPage = (props: {
  nft: UnifiedNFTBalance;
  operations: EstimatedAccountOperations;
  chainId?: ChainFullId;
}) => {
  const { operations: initialOperations, nft, chainId = ChainFullIds.TEZOS } = props;

  const { fee, operations, estimationFailed, isLoading, form, signer, onSign } = useSignPageHelpers(
    initialOperations,
    chainId
  );
  const color = useColor();
  const hideBalance = useBreakpointValue({ base: true, md: false });

  const fa2Operation = operations.operations[0] as FA2Transfer;
  const { namespace } = parseChainFullId(chainId);
  const { recipient, amount } = fa2Operation;

  const isSafeSendEnabled = form.watch("isSafeSendEnabled");
  const safeSendFee = form.watch("safeSendFee");
  const displayFee = isSafeSendEnabled ? safeSendFee : fee;

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <SignPageHeader />
          <ModalBody>
            <VStack alignItems="start" spacing="24px">
              <Flex flexDirection="column" gap="12px" width="full" color={color("700")}>
                <NFTTile nft={nft} />
                <Flex gap="4px">
                  <Heading size="md">Quantity:</Heading>
                  <Text data-testid="nft-amount">{amount}</Text>
                  <Text color={color("400")}>/</Text>
                  <Text marginRight="auto" data-testid="nft-owned">
                    {nft.balance}
                  </Text>
                  <AsyncSignPageFee chainId={chainId} fee={displayFee} />
                </Flex>
              </Flex>
              <Divider />
              <FormControl>
                <FormLabel>From</FormLabel>
                <AddressTile address={getSender(operations, chainId)} hideBalance={hideBalance} />
              </FormControl>
              <FormControl>
                <FormLabel>To</FormLabel>
                <AddressTile address={recipient} hideBalance={hideBalance} />
              </FormControl>
              {namespace === ChainNamespaces.TEZOS && (
                <SafeSendTile balance={nft.balance} operation={fa2Operation} signer={signer} />
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <SignButton
              isDisabled={estimationFailed}
              isLoading={isLoading}
              onSubmit={onSign}
              signer={signer}
              text="Confirm"
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
